import { createSlice, isAnyOf, type PayloadAction } from '@reduxjs/toolkit'
import { actionUserLogin, actionUserLogout } from '@shared-core/features/auth/actions/global'
import {
  apiLoginByPhoneOtp,
  apiLoginTma,
  apiRefreshAuthTokens,
  apiRequestAuthOtp,
} from '@shared-core/features/auth/api'
import { modelAuthLoginResponse } from '@shared-core/features/auth/models'
import { navigation, ScreensList, StackMode } from '@shared-core/navigation'
import { createAsyncThunk } from '@shared-core/store/utils'

export const storeKey = '@redux/auth'

export type AuthUser = {
  login: string
}

export type AuthState = {
  isAuthorized: boolean
  accessToken: string | null
  refreshToken: string | null
  user: AuthUser | null
}

const initialState: AuthState = {
  isAuthorized: false,
  accessToken: null,
  refreshToken: null,
  user: null,
}

export const requestOtp = createAsyncThunk('auth/requestOtp', async ({ phone }: { phone: string }) => {
  const response = await apiRequestAuthOtp({ data: { phone } })

  if (response?.status !== 'ok' || !response.data) {
    throw new Error('Не удалось отправить код')
  }

  return response.data
})

export const login = createAsyncThunk(
  'auth/login',
  async ({ phone, otp, totp }: { phone: string; otp: string; totp?: string }) => {
    const response = await apiLoginByPhoneOtp({ data: { phone, otp, ...(totp ? { totp } : {}) } })

    if (!response?.data) {
      throw new Error('Неверный или просроченный код')
    }

    return {
      accessToken: response.data.accessToken,
      refreshToken: response.data.refreshToken,
      user: { login: phone } satisfies AuthUser,
    }
  },
)

export const logout = createAsyncThunk('auth/logout', (_, { dispatch }) => {
  dispatch(actionUserLogout())
  navigation.reset(StackMode.LOGOUT, { screen: ScreensList.HOME_START })
})

export const loginTma = createAsyncThunk('auth/loginTma', async ({ initData }: { initData: string }) => {
  if (!initData.trim()) throw new Error('Нет данных для входа через Telegram')

  const response = await apiLoginTma({ data: { init_data_raw: initData } })
  const result = modelAuthLoginResponse.safeParse(response)

  if (
    !result.success ||
    result.data.status !== 'ok' ||
    Object.keys(result.data.errors).length > 0 ||
    !result.data.data.accessToken.trim() ||
    !result.data.data.refreshToken.trim() ||
    result.data.data.id === null
  ) {
    throw new Error('Не удалось войти через Telegram')
  }

  return {
    accessToken: result.data.data.accessToken,
    refreshToken: result.data.data.refreshToken,
    user: { login: String(result.data.data.id) } satisfies AuthUser,
  }
})

export const renewAccessToken = createAsyncThunk('auth/renewToken', async (_, { getState, dispatch }) => {
  const refreshToken = getState()[storeKey].refreshToken

  if (!refreshToken) return false

  const response = await apiRefreshAuthTokens({ data: { refreshToken } })

  if (!response?.data) return false
  dispatch(
    authSlice.actions.setTokens({
      accessToken: response.data.accessToken,
      refreshToken: response.data.refreshToken,
    }),
  )
  return true
})

export const authSlice = createSlice({
  name: storeKey,
  initialState,
  reducers: {
    setAccessToken(state, action: PayloadAction<string | null>) {
      state.accessToken = action.payload
    },
    setTokens(state, action: PayloadAction<{ accessToken: string; refreshToken: string }>) {
      state.accessToken = action.payload.accessToken
      state.refreshToken = action.payload.refreshToken
    },
  },
  extraReducers: (builder) => {
    builder.addCase(actionUserLogin, (state) => {
      state.isAuthorized = true
    })
    builder.addCase(actionUserLogout, (state) => {
      state.isAuthorized = false
      state.accessToken = null
      state.refreshToken = null
      state.user = null
    })
    builder.addMatcher(isAnyOf(login.fulfilled, loginTma.fulfilled), (state, action) => {
      state.isAuthorized = true
      state.accessToken = action.payload.accessToken
      state.refreshToken = action.payload.refreshToken
      state.user = action.payload.user
    })
  },
})

export const { setAccessToken, setTokens } = authSlice.actions

// Сессию персистим: после рестарта ENTRY_LOADING решает, вести в LOGOUT или AUTHORIZED.
export const whitelist = ['isAuthorized', 'accessToken', 'refreshToken', 'user']
